import { AnimatedCurrencyValue } from '@/components';
import { trendVariantInverted } from '@/utils';
import {
  ArrowRightIcon,
  Box,
  Card,
  CurrencyValue,
  Flex,
  KpiBody,
  KpiFooter,
  KpiHeader,
  PercentageValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useIsMobile
} from '@blockanalitica/ui';
import {
  CurrencyValueOptions,
  Notation
} from '@blockanalitica/ui/package/types/src/components/molecules/CurrencyValue/CurrencyValue';
import { PercentageValueOptions } from '@blockanalitica/ui/package/types/src/components/molecules/PercentageValue';
import { ThemedComponentWrapperProps } from '@blockanalitica/ui/package/types/src/theme/ThemedComponentWrapper';
import _ from 'lodash';
import { Fragment, ReactNode, useMemo } from 'react';
import styled from 'styled-components';
const Arrow = styled(ArrowRightIcon)`
  align-self: flex-end;
  color: ${(props) => props.theme.colors.muted};
`;

export interface Options {
  variant?: string;
  currencyValueOptions?: CurrencyValueOptions;
  percentageValueOptions?: PercentageValueOptions;
  valueFontSize?: string;
  linkArrow?: boolean;
  notation?: Notation;
}

export interface IconKpiCardProps extends Omit<
  ThemedComponentWrapperProps,
  'title' | 'name'
> {
  leftIcon?: ReactNode;
  field: string;
  title: ReactNode;
  titleRight?: ReactNode;
  data: Record<string, string | number | null>;
  options?: Options;
  footerContent?: ReactNode;
}

export default function IconNumberKpiCard({
  title,
  titleRight,
  leftIcon,
  field,
  data,
  options,
  footerContent,
  ...rest
}: IconKpiCardProps) {
  const isMobile = useIsMobile();
  const fields = useMemo(
    () => ({
      fieldBase: field,
      trendBase: `${field}_change`,
      trendPercBase: `${field}_change_percentage`
    }),
    [field]
  );

  const bodyTrendValue = useMemo<number | null>(() => {
    if (Math.abs(Number(data[fields.trendPercBase])) > 0) {
      return Number(data[fields.trendPercBase]);
    }

    return null;
  }, [data, fields.trendPercBase]);

  const bodyTrendValueChange = useMemo<number | null>(() => {
    if (Math.abs(Number(data[fields.trendBase])) > 0) {
      return Number(data[fields.trendBase]);
    }

    return null;
  }, [data, fields.trendBase]);

  const mergedOptions = useMemo(
    () =>
      _.merge(
        {
          valueFontSize: '4',
          linkArrow: true
        },
        options
      ),
    [options]
  );

  return (
    <Card {...rest} flexDirection="row">
      <Flex className="cardContentWrapper" gap="s" flex="1">
        <Flex alignItems="center">{leftIcon}</Flex>
        <Flex display="flex" flex="1" gap="xs" flexDirection="column">
          <KpiHeader>
            <Flex justifyContent="space-between" flex="1">
              {title}
              {titleRight}
            </Flex>
          </KpiHeader>
          {isMobile ? (
            <Fragment>
              <Flex flexDirection="row" flex="1">
                <Flex flexDirection="column" gap="xs" flex="1">
                  <KpiBody
                    gap="s"
                    alignItems="center"
                    justifyContent="space-between">
                    <Flex
                      alignItems="baseline"
                      gap="xs"
                      width="95%"
                      fontSize={mergedOptions.valueFontSize}>
                      <AnimatedCurrencyValue
                        value={Number(data[fields.fieldBase])}
                        options={mergedOptions?.currencyValueOptions}
                      />

                      <Box fontSize="1">
                        <Tooltip>
                          <TooltipTrigger>
                            <CurrencyValue
                              value={bodyTrendValueChange}
                              options={{
                                currency: '',
                                notation: 'compact',
                                tooltip: false,
                                trend: true,
                                iconSize: '0',
                                trendVariant: trendVariantInverted
                              }}
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            <PercentageValue
                              value={bodyTrendValue}
                              options={{
                                tooltip: false,
                                formatPercentageOptions: {
                                  maximumFractionDigits: 2
                                }
                              }}
                            />
                          </TooltipContent>
                        </Tooltip>
                      </Box>
                    </Flex>
                  </KpiBody>
                </Flex>
              </Flex>
              <Flex flexDirection="column" gap="xs" flex="1">
                <Flex fontSize="1" justifyContent="space-between" gap="xs">
                  <KpiFooter>{footerContent}</KpiFooter>

                  {mergedOptions?.linkArrow ? (
                    <Arrow className="kpiCardArrowLink" />
                  ) : null}
                </Flex>
              </Flex>
            </Fragment>
          ) : (
            <Flex flexDirection="row" flex="1">
              <Flex flexDirection="column" gap="xs" flex="1">
                <KpiBody
                  gap="s"
                  alignItems="center"
                  justifyContent="space-between">
                  <Flex
                    alignItems="baseline"
                    gap="xs"
                    width="95%"
                    fontSize={mergedOptions.valueFontSize}>
                    <AnimatedCurrencyValue
                      value={Number(data[fields.fieldBase])}
                      options={mergedOptions?.currencyValueOptions}
                    />
                    <Box fontSize="1">
                      <Tooltip>
                        <TooltipTrigger>
                          <CurrencyValue
                            value={bodyTrendValueChange}
                            options={{
                              currency: '',
                              notation: 'compact',
                              tooltip: false,
                              trend: true,
                              iconSize: '0',
                              trendVariant: trendVariantInverted
                            }}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <PercentageValue
                            value={bodyTrendValue}
                            options={{
                              tooltip: false,
                              formatPercentageOptions: {
                                maximumFractionDigits: 2
                              }
                            }}
                          />
                        </TooltipContent>
                      </Tooltip>
                    </Box>
                  </Flex>
                </KpiBody>
                <KpiFooter>{footerContent}</KpiFooter>
              </Flex>
              {mergedOptions?.linkArrow ? (
                <Arrow className="kpiCardArrowLink" />
              ) : null}
            </Flex>
          )}
        </Flex>
      </Flex>
    </Card>
  );
}
